import React, { useContext, useEffect, useState } from "react";
import { useMessageSource } from "react-message-source";
import { ApplicationFlows } from "../../route/application/authenticated/application-flow/types";
import ApplicationPageContent from "../../ui/layout/page/content/application";
import ContentFooter from "../../ui/layout/page/content/footer";
import NavigationBar from "../../ui/layout/page/content/footer/navigation-bar";
import ContentHeader from "../../ui/layout/page/content/header";
import { Form, Formik } from "formik";
import FormContainer from "../../ui/layout/page/forms-container/form-base-container";
import FormFullWidthContainer from "../../ui/layout/page/forms-container/form-full-width-container";
import CheckboxContainer from "../../ui/layout/page/forms-container/form-item-container/checkbox-container";
import RadioButtonContainer from "../../ui/layout/page/forms-container/form-item-container/radio-button-container/input-container";
import TypographyItemContainer from "../../ui/layout/page/typography-container/typography-item-container";
import FlowHeader from "../../ui/widgets/flow-header";
import Info from "../../ui/widgets/info";
import CustomLink from "../../ui/widgets/link";
import CustomList from "../../ui/widgets/list";
import CustomListItem from "../../ui/widgets/list/list-item";
import CustomTypography from "../../ui/widgets/typography";
import { mapYesNoDecisionToEvent } from "../../util/tracking/eventActionMapper";
import GTM from "../../util/tracking/gtm";
import { EventCategory } from "../../util/tracking/types";
import { CardholderModel } from "../cardholder-basic-information/cardholder-form/types";
import CheckboxField from "../shared/components/formik/checkbox";
import RadioButtonField from "../shared/components/formik/radiobutton";
import ProductImage from "../shared/components/product-image";
import { usePageTitle } from "../shared/hooks/usePageTitle";
import { RejectionReason } from "./types";
import { SignModel } from "./types";
import useSignValidationScheme from "./validator";
import { LanguageContext } from "../../context/languageContext";
import SignatureDigitalIcon from "../../resources/images/scd-signature-digital-icon.svg";
import SignatureManualIcon from "../../resources/images/scd-signature-manual-icon.svg";
import { createApplicationPdfLink } from "../../util/pdf";
import InvalidFormError, {
  isSubmitErrorMessageShown,
} from "../../ui/layout/page/content/footer/form-error/invalid-form-error";
import {
  CompanyAttachment as CompanyAttachmentDocument,
  PersonAttachment as PersonAttachmentDocument,
} from "../../api/commercial/generated-types";
import { ProductRefType } from "../../context/product-context/types";
import { useFeatureToggles } from "../shared/hooks/useFeatureToggles";
import { Feature } from "../../context/types";
import { RequiredAttachmentList } from "../../ui/widgets/required-attachment-list";
import Typography from "../../ui/widgets/typography";
import { RequiredDocumentItem } from "../document-upload/required-document-item";
import CountableStep from "../../ui/widgets/countable-step";
import { Link } from "@mui/material";
import SummaryStepImage from "../shared/components/summary-step-image";

import PostalAddress from "../../ui/widgets/postal-address";
import { YesNo } from "../general-company-information/types.tsx";

export default function SigningPage({
  flowType,
  context,
  previous,
  next,
}: {
  flowType: ApplicationFlows;
  previous: () => void;
  next: (values: SignModel) => void;
  context: {
    companyName: string;
    status: string;
    rejectionReason: RejectionReason;
    canDigitallySign: boolean;
    requiredCompanyAttachmentsBAA: CompanyAttachmentDocument[];
    requiredPersonAttachmentsBAA: PersonAttachmentDocument[];
    requiredAttachmentsCA: PersonAttachmentDocument[];
    cardholder: CardholderModel;
    basicApplicationRef: string;
    cardApplicationRef: string;
    selectedProductRef: ProductRefType;
  };
}): React.JSX.Element {
  const { getMessage, getMessageWithNamedParams } = useMessageSource();
  const [basicApplicationPdfLink, setBasicApplicationPdfLink] = useState<
    string | null
  >(null);
  const [cardApplicationPdfLink, setCardApplicationPdfLink] = useState<
    string | null
  >(null);

  const {
    companyName,
    canDigitallySign,
    requiredCompanyAttachmentsBAA,
    requiredPersonAttachmentsBAA,
    requiredAttachmentsCA,
    cardholder: { firstName, lastName },
    selectedProductRef,
    basicApplicationRef,
    cardApplicationRef,
  } = context;

  const canUploadAttachments = useFeatureToggles(Feature.ATTACHMENT_UPLOAD);

  useEffect(() => {
    const run = async () => {
      const basicAccountPdfLink =
        await createApplicationPdfLink(basicApplicationRef);
      setBasicApplicationPdfLink(basicAccountPdfLink);

      const cardApplicationPdfLink =
        await createApplicationPdfLink(cardApplicationRef);
      setCardApplicationPdfLink(cardApplicationPdfLink);
    };
    run();
  }, [basicApplicationRef, cardApplicationRef]);

  const productFullName = getMessage(`Product.${selectedProductRef}.title`);

  const { language } = useContext(LanguageContext);

  const initial: SignModel = {
    wantsToDigitallySign: "yes",
    acceptTermsAndConditions: false,
  };

  const isNotInAdditionalCardholderFlow =
    flowType !== ApplicationFlows.ADDITIONAL_CA;

  return (
    <>
      <FlowHeader
        title={usePageTitle(flowType)}
        image={<ProductImage product={selectedProductRef} />}
        subTitle={context.companyName}
      />
      <ApplicationPageContent>
        <TypographyItemContainer>
          <ContentHeader
            title={getMessage("CardholderConfirmation.summary-title")}
          />
        </TypographyItemContainer>
        <SummaryStepImage />
        <Formik
          key={language}
          enableReinitialize
          initialValues={initial}
          onSubmit={async (values, formikActions) => {
            await next(values);
            formikActions.setSubmitting(false);
          }}
          validationSchema={useSignValidationScheme(canDigitallySign)}
        >
          {({ values, handleSubmit, isSubmitting, submitCount, errors }) => {
            const handwrittenSignatureView = (
              <>
                <FormFullWidthContainer>
                  <CountableStep number={1}>
                    <>
                      <TypographyItemContainer>
                        <CustomTypography variant="h4">
                          {getMessage(
                            "CardholderConfirmation.summary-print-and-description",
                          )}
                        </CustomTypography>
                      </TypographyItemContainer>

                      {isNotInAdditionalCardholderFlow && (
                        <TypographyItemContainer>
                          <CustomLink
                            href={basicApplicationPdfLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {getMessageWithNamedParams(
                              "CardholderConfirmation.print-out-as-master-account",
                              { companyName },
                            )}
                          </CustomLink>
                        </TypographyItemContainer>
                      )}

                      <CustomLink
                        href={cardApplicationPdfLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {getMessageWithNamedParams(
                          "CardholderConfirmation.print-out-as-card-application",
                          {
                            firstName,
                            lastName,
                          },
                        )}
                      </CustomLink>
                    </>
                  </CountableStep>

                  <CountableStep number={2}>
                    <>
                      <TypographyItemContainer>
                        <CustomTypography variant="h4">
                          {getMessage(
                            "CardholderConfirmation.summary-person-id-authentication",
                          )}
                        </CustomTypography>

                        <CustomTypography>
                          {getMessage(
                            "CardholderConfirmation.summary-person-id-authentication-required-persons",
                          )}
                        </CustomTypography>
                      </TypographyItemContainer>

                      <TypographyItemContainer>
                        <Typography>
                          {firstName} {lastName}
                        </Typography>
                      </TypographyItemContainer>

                      <TypographyItemContainer>
                        <CustomLink
                          href={getMessage(
                            "CardholderConfirmation.authenticated-copy-info-link-url",
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {getMessage(
                            "CardholderConfirmation.authenticated-copy-info-link-title",
                          )}
                        </CustomLink>
                      </TypographyItemContainer>

                      <CustomLink
                        href={getMessage(
                          "CardholderConfirmation.authenticated-copy-alternative-link-url",
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {getMessage(
                          "CardholderConfirmation.authenticated-copy-alternative-link-title",
                        )}
                      </CustomLink>
                    </>
                  </CountableStep>

                  {canUploadAttachments && (
                    <CountableStep number={3}>
                      <>
                        <TypographyItemContainer>
                          <CustomTypography variant="h4">
                            {getMessage(
                              "CardholderConfirmation.summary-complete-the-authentication",
                            )}
                          </CustomTypography>
                          <CustomTypography component="span">
                            {getMessage(
                              "CardholderConfirmation.summary-encode-documents-part-1",
                            )}
                          </CustomTypography>{" "}
                          <Link
                            className="scd-link"
                            href="#"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              e.preventDefault();
                              next({
                                wantsToDigitallySign: "no",
                                acceptTermsAndConditions: false,
                              });
                            }}
                          >
                            {getMessage(
                              "CardholderConfirmation.summary-encode-documents-link-text",
                            )}
                          </Link>{" "}
                          <CustomTypography component="span">
                            {getMessage(
                              "CardholderConfirmation.summary-encode-documents-part-2",
                            )}
                          </CustomTypography>
                        </TypographyItemContainer>

                        <RequiredAttachmentList
                          requiredPersonBaaAttachments={
                            requiredPersonAttachmentsBAA
                          }
                          requiredPersonCaAttachments={requiredAttachmentsCA}
                          requiredCompanyAttachments={
                            requiredCompanyAttachmentsBAA
                          }
                        />
                      </>
                    </CountableStep>
                  )}

                  <CountableStep number={canUploadAttachments ? 4 : 3}>
                    <>
                      <TypographyItemContainer>
                        <CustomTypography variant="h4">
                          {getMessage("PostalAddress.title")}
                        </CustomTypography>
                      </TypographyItemContainer>

                      <TypographyItemContainer>
                        <RequiredDocumentItem
                          content={getMessage(
                            "DocumentUpload.post-document-signed-master-account",
                          )}
                        />
                        <RequiredDocumentItem
                          content={getMessage(
                            "DocumentUpload.post-document-signed-card-application",
                          )}
                        />

                        {canUploadAttachments ? (
                          <RequiredDocumentItem
                            content={getMessage(
                              "DocumentUpload.post-document-all-post-documents",
                            )}
                          />
                        ) : (
                          <></>
                        )}
                      </TypographyItemContainer>

                      <PostalAddress />
                    </>
                  </CountableStep>

                  <CustomTypography>
                    {getMessage("PostalAddress.thank-you-note")}
                  </CustomTypography>
                </FormFullWidthContainer>
                <ContentFooter>
                  <InvalidFormError
                    isDisplayed={isSubmitErrorMessageShown(submitCount, errors)}
                  />
                  <NavigationBar
                    isSubmitting={isSubmitting}
                    onBack={previous}
                    onNext={handleSubmit}
                    backButtonText={getMessage("Onboarding.back")}
                    nextButtonText={getMessage("Onboarding.finish")}
                  />
                </ContentFooter>
              </>
            );
            const digitalSignatureView = (
              <>
                <Info title={getMessage("Swisscom.off-hours.title")}>
                  {getMessage("Swisscom.off-hours.description")}
                </Info>

                <FormFullWidthContainer hasAdditionalTopMargin={true}>
                  <TypographyItemContainer>
                    <CustomTypography variant="h4">
                      {getMessage(
                        "Sign.digital-signature-info-title",
                        productFullName,
                      )}
                    </CustomTypography>
                  </TypographyItemContainer>
                  <CustomTypography variant="body1">
                    <CustomList isBulletsList>
                      <CustomListItem>
                        {getMessage("Sign.digital-signature-info-1")}
                      </CustomListItem>
                      <CustomListItem>
                        {getMessage("Sign.digital-signature-info-2")}
                      </CustomListItem>
                      <CustomListItem>
                        {getMessage("Sign.digital-signature-info-3")}
                      </CustomListItem>
                    </CustomList>
                  </CustomTypography>
                </FormFullWidthContainer>

                <FormFullWidthContainer hasAdditionalTopMargin={true}>
                  <TypographyItemContainer>
                    <CustomTypography variant="h4">
                      {getMessage("Sign.digital-signature-prerequisites-title")}
                    </CustomTypography>
                  </TypographyItemContainer>
                  <CustomTypography variant="body1">
                    <CustomList isBulletsList>
                      <CustomListItem>
                        {getMessage("Sign.digital-signature-prerequisites-1")}
                      </CustomListItem>
                      <CustomListItem>
                        {getMessage("Sign.digital-signature-prerequisites-2")}
                      </CustomListItem>
                      <CustomListItem>
                        {getMessage("Sign.digital-signature-prerequisites-3")}
                      </CustomListItem>
                    </CustomList>
                  </CustomTypography>
                </FormFullWidthContainer>
                <FormFullWidthContainer hasAdditionalTopMargin={true}>
                  <CheckboxContainer>
                    <CheckboxField
                      forceReRender
                      name="acceptTermsAndConditions"
                      label={
                        <span className="checkbox-text">
                          {getMessage("TermsAndConditions.accept-1")}
                          <CustomLink noicon href={"#"} target={"_blank"}>
                            {getMessage(
                              "Sign.digital-signature-terms-and-conditions",
                            )}
                          </CustomLink>
                          {getMessage("TermsAndConditions.accept-2")}
                        </span>
                      }
                    />
                  </CheckboxContainer>
                </FormFullWidthContainer>
                <ContentFooter>
                  <InvalidFormError
                    isDisplayed={isSubmitErrorMessageShown(submitCount, errors)}
                  />
                  <NavigationBar
                    isSubmitting={isSubmitting}
                    onBack={previous}
                    onNext={handleSubmit}
                    backButtonText={getMessage("Onboarding.back")}
                    nextButtonText={getMessage(
                      "Sign.digital-signature-submit-button",
                    )}
                  />
                </ContentFooter>
              </>
            );

            let currentView = null;
            if (canDigitallySign && values.wantsToDigitallySign === "yes") {
              currentView = digitalSignatureView;
            } else {
              currentView = handwrittenSignatureView;
            }

            return (
              <Form>
                <FormContainer hasAdditionalTopMargin={true}>
                  {canDigitallySign && (
                    <RadioButtonContainer>
                      <RadioButtonField
                        id="wantsToDigitallySign"
                        title={getMessage("Sign.digital-signing-title")}
                        name="wantsToDigitallySign"
                        onChangeCallback={(_, newValue) => {
                          GTM.trackEvent(
                            EventCategory.DigitalOrHandwrittenSignature,
                            mapYesNoDecisionToEvent(newValue),
                          );
                        }}
                        values={[
                          {
                            id: "sign-digitally-yes",
                            value: YesNo.YES,
                            label: getMessage("Sign.choose-digital-signature"),
                            icon: SignatureDigitalIcon,
                          },
                          {
                            id: "sign-digitally-no",
                            value: YesNo.NO,
                            label: getMessage(
                              "Sign.choose-handwritten-signature",
                            ),
                            icon: SignatureManualIcon,
                          },
                        ]}
                      />
                    </RadioButtonContainer>
                  )}
                </FormContainer>
                {currentView}
              </Form>
            );
          }}
        </Formik>
      </ApplicationPageContent>
    </>
  );
}
